import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, User as UserIcon, Calendar } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { RoleBadge, UserStatusBadge, useOrganizationUsers } from '@/modules/users';
import { OrganizationUserDto } from '@cargovibe/shared-types';
import { formatFullName } from '@/shared/services/formatFullName';

interface DriverProfileDialogProps {
  driverId: string | null;
  fallbackName?: string;
  onClose: () => void;
}

export function DriverProfileDialog({
  driverId,
  fallbackName,
  onClose,
}: DriverProfileDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const { data: users } = useOrganizationUsers();

  const driver: OrganizationUserDto | null = driverId
    ? (users?.find((user) => user.id === driverId) ?? null)
    : null;

  const handleOpenChange = (open: boolean): void => {
    if (!open) onClose();
  };

  const driverName: string = driver ? formatFullName(driver.firstname, driver.lastname) : '';

  return (
    <Dialog open={!!driverId} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('bookings.driverProfile.title')}</DialogTitle>
          <DialogDescription>{driverName || fallbackName || ''}</DialogDescription>
        </DialogHeader>

        {!driver ? (
          <p className="py-6 text-sm text-muted-foreground">
            {t('bookings.driverProfile.notFound')}
          </p>
        ) : (
          <div className="grid gap-4 py-2">
            <div className="flex items-center gap-3">
              <UserIcon size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium">{driverName}</span>
              <div className="ml-auto flex items-center gap-2">
                <RoleBadge role={driver.role} />
                <UserStatusBadge status={driver.status} />
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Mail size={16} className="text-muted-foreground" />
              {driver.email ? (
                <a href={`mailto:${driver.email}`} className="text-primary hover:underline">
                  {driver.email}
                </a>
              ) : (
                <span className="text-muted-foreground">{t('bookings.driverProfile.noEmail')}</span>
              )}
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Phone size={16} className="text-muted-foreground" />
              {driver.mobileNumber ? (
                <a href={`tel:${driver.mobileNumber}`} className="text-primary hover:underline">
                  {driver.mobileNumber}
                </a>
              ) : (
                <span className="text-muted-foreground">{t('bookings.driverProfile.noPhone')}</span>
              )}
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Calendar size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('users.bookingLimit')}:</span>
              <span className="font-medium">{driver.maxBookingsPerMonth}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
